import type { TimeBlockRecord } from '../model/types';
import { addDays } from '../time/date';
import { absoluteMin, confirmedBlocksIn } from './segments';

/** Pickers rank projects by their use in this many personal dates, today included. */
export const USAGE_WINDOW_DAYS = 14;

/**
 * `projects` ordered for choosing: most confirmed blocks in the last `windowDays` personal dates
 * (ending at `today`) first; equal counts: the project whose latest such block starts later first;
 * projects without any such block, and any remaining tie, keep their input order.
 * Drafts do not count: they are not records yet (requirements §1.3, honest recording).
 */
export function rankProjectsByUsage<P extends { readonly id: string }>(
  projects: readonly P[],
  blocks: readonly TimeBlockRecord[],
  today: string,
  windowDays: number = USAGE_WINDOW_DAYS,
): P[] {
  const range = { from: addDays(today, 1 - windowDays), to: today };
  const count = new Map<string, number>();
  const latest = new Map<string, number>();
  for (const block of confirmedBlocksIn(blocks, range)) {
    const { projectId, personalDate, startMin } = block.data;
    count.set(projectId, (count.get(projectId) ?? 0) + 1);
    latest.set(projectId, Math.max(latest.get(projectId) ?? -Infinity, absoluteMin(personalDate, startMin)));
  }
  return projects
    .map((project, position) => ({ project, position }))
    .sort(
      (a, b) =>
        (count.get(b.project.id) ?? 0) - (count.get(a.project.id) ?? 0) ||
        (latest.get(b.project.id) ?? -Infinity) - (latest.get(a.project.id) ?? -Infinity) ||
        a.position - b.position,
    )
    .map((entry) => entry.project);
}

/**
 * The latest personal date with a confirmed block, per project id (projects never used are
 * absent). Feeds `isChildFolded`; drafts do not count, like in `rankProjectsByUsage`.
 */
export function lastUsedDateByProject(blocks: readonly TimeBlockRecord[]): Map<string, string> {
  const latest = new Map<string, string>();
  for (const block of confirmedBlocksIn(blocks)) {
    const { projectId, personalDate } = block.data;
    const known = latest.get(projectId);
    if (known === undefined || personalDate > known) latest.set(projectId, personalDate);
  }
  return latest;
}
